/**
 * Etats de vue : liste vide, alerte, erreur d'API. Une vue n'est jamais
 * blanche : elle dit ce qui se passe et, quand c'est possible, propose une suite.
 */
import { useCallback, useRef } from 'react';
import type { ReactNode, Ref, RefCallback, RefObject } from 'react';
import { CircleCheck, Info, TriangleAlert } from 'lucide-react';
import { detailsErreur, messageErreur } from '../../lib/api';
import { Bouton } from './boutons';
import { focaliserOuReplier } from './couche';

export function EtatVide({
  titre,
  description,
  action,
  icone,
  baliseTitre: BaliseTitre = 'p',
}: {
  titre: string;
  description?: ReactNode;
  action?: ReactNode;
  /** Icone Lucide d'illustration, agrandie a 24 px dans un cercle d'accent. */
  icone?: ReactNode;
  /** `h1` quand l'etat tient lieu de page entiere (page introuvable, ecran en erreur). */
  baliseTitre?: 'p' | 'h1' | 'h2' | 'h3';
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      {icone && (
        <span
          aria-hidden="true"
          className="mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-accent-50 text-accent-600 [&_svg]:size-6"
        >
          {icone}
        </span>
      )}
      <BaliseTitre className="text-md font-semibold text-encre">{titre}</BaliseTitre>
      {description && <p className="max-w-md text-sm text-encre-discrete">{description}</p>}
      {action && <div className="mt-3 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

export type TonBandeau = 'info' | 'succes' | 'avertissement' | 'danger' | 'neutre';

const STYLES_BANDEAU: Record<TonBandeau, string> = {
  info: 'border-info-200 bg-info-50 text-info-800',
  succes: 'border-succes-200 bg-succes-50 text-succes-800',
  avertissement: 'border-avertissement-200 bg-avertissement-50 text-avertissement-800',
  danger: 'border-danger-200 bg-danger-50 text-danger-800',
  neutre: 'border-bordure bg-surface-douce text-encre-douce',
};

/**
 * Bandeau d'alerte. L'icone et le titre portent le sens : la couleur seule ne
 * distingue jamais un avertissement d'une confirmation.
 */
export function Bandeau({
  ton = 'info',
  titre,
  children,
  actions,
  role = 'none',
  focalisable = false,
  ref,
  className = '',
}: {
  ton?: TonBandeau;
  titre?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /**
   * Statique par defaut : un bandeau affiche avec la page n'a rien a annoncer.
   * `alert` ou `status` seulement pour un bandeau qui apparait en reponse a
   * une action (erreur, confirmation).
   */
  role?: 'alert' | 'status' | 'none';
  /**
   * Cible de focus par programme (`ref`), hors de la tabulation : le bandeau
   * qui remplace le controle qui avait le focus le recoit. Pas d'anneau, comme
   * un titre focalise : ce n'est pas un controle.
   */
  focalisable?: boolean;
  ref?: Ref<HTMLDivElement>;
  className?: string;
}) {
  const Icone = ton === 'succes' ? CircleCheck : ton === 'info' || ton === 'neutre' ? Info : TriangleAlert;
  return (
    <div
      ref={ref}
      role={role === 'none' ? undefined : role}
      tabIndex={focalisable ? -1 : undefined}
      className={`flex flex-wrap items-start gap-3 rounded-md border px-3.5 py-3 text-sm ${focalisable ? 'focus:outline-none' : ''} ${STYLES_BANDEAU[ton]} ${className}`}
    >
      <Icone aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-0.5">
        {titre && <p className="font-semibold">{titre}</p>}
        {children && <div className="text-sm leading-relaxed">{children}</div>}
      </div>
      {/* Sous 640 px, les actions passent sous le texte, alignees sur lui : a cote, elles l'ecraseraient. */}
      {actions && <div className="flex w-full shrink-0 flex-wrap items-center gap-2 pl-7 sm:w-auto sm:pl-0">{actions}</div>}
    </div>
  );
}

/**
 * Ref d'un bouton de rechargement (« Reessayer », « Recharger ») qui part avec
 * son message une fois les donnees relues : s'il avait le focus, celui-ci se
 * replie sur la region rechargee (titre, liste), a defaut sur `main`, jamais
 * sur la page entiere. Le nettoyage de la ref (React 19) passe au retrait du
 * bouton, encore dans le document : que son message disparaisse ou qu'il
 * reste sans lui (un 404 n'offre plus de nouvel essai).
 */
export function useRepliDuFocus(refRegion?: RefObject<HTMLElement | null>): RefCallback<HTMLElement> {
  // Lue au retrait, hors des dependances : un rappel renouvele detacherait le bouton encore focalise, et deplacerait le focus a tort.
  const refCible = useRef(refRegion);
  refCible.current = refRegion;
  return useCallback((bouton: HTMLElement | null) => () => {
    if (bouton !== null && document.activeElement === bouton) focaliserOuReplier(refCible.current?.current);
  }, []);
}

/** Affiche une erreur d'API de facon lisible plutot que dans la console. */
export function MessageErreur({
  erreur,
  titre = 'Une erreur est survenue',
  onReessayer,
  message = messageErreur,
  refFocusRetour,
}: {
  erreur: unknown;
  titre?: string;
  onReessayer?: () => void;
  /** Phrase de l'erreur ; `messageEcritureLead` explique un 404 recu en ecrivant sur un lead. */
  message?: (erreur: unknown) => string;
  /** Region rechargee (titre, conteneur) : repli du focus quand le message part avec « Reessayer » ; a defaut, `main`. */
  refFocusRetour?: RefObject<HTMLElement | null>;
}) {
  const refReessayer = useRepliDuFocus(refFocusRetour);
  if (!erreur) return null;
  const details = detailsErreur(erreur);
  return (
    <Bandeau
      ton="danger"
      role="alert"
      titre={titre}
      actions={
        onReessayer ? (
          <Bouton ref={refReessayer} taille="sm" onClick={onReessayer}>
            Réessayer
          </Bouton>
        ) : undefined
      }
    >
      <p>{message(erreur)}</p>
      {details.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4">
          {details.slice(0, 6).map((detail, index) => (
            <li key={index}>{detail}</li>
          ))}
        </ul>
      )}
    </Bandeau>
  );
}
